import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, X, Trash2, MessageCircle, Copy, Check } from "lucide-react";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import { useConfig } from "@/context/ConfigContext";
import { useAuth } from "@/context/AuthContext";

const HOURS = Array.from({ length: 10 }, (_, i) => 10 + i); // 10..19 (last slot starts 19:00)
const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay(); // 0 sun ... 6 sat
  const diff = day === 0 ? -6 : 1 - day; // Monday as start
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}
function ymd(d) {
  return d.toISOString().slice(0, 10);
}
function addDays(d, n) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
}

export default function Agenda() {
  const { user } = useAuth();
  const [anchor, setAnchor] = useState(startOfWeek(new Date()));
  const [turnos, setTurnos] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [filterProf, setFilterProf] = useState("todos");

  const weekDays = useMemo(() => Array.from({ length: 6 }, (_, i) => addDays(anchor, i)), [anchor]);

  const load = async () => {
    try {
      const desde = ymd(weekDays[0]);
      const hasta = ymd(weekDays[5]);
      const params = { fecha_desde: desde, fecha_hasta: hasta };
      if (filterProf !== "todos") params.profesional_id = filterProf;
      const [t, p, pr] = await Promise.all([
        api.get("/turnos", { params }),
        api.get("/pacientes"),
        api.get("/profesionales"),
      ]);
      setTurnos(t.data);
      setPacientes(p.data);
      setProfesionales(pr.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [anchor, filterProf]);

  const openCreate = (fecha, hora) => {
    setPrefill({ fecha, hora });
    setModalOpen(true);
  };

  const cellTurnos = (dateStr, hour) => {
    const hh = String(hour).padStart(2, "0");
    return turnos.filter((t) => t.fecha === dateStr && t.hora?.startsWith(hh));
  };

  const rangeLabel = `${weekDays[0].toLocaleDateString("es-PY", { day: "numeric", month: "short" })} — ${weekDays[5].toLocaleDateString("es-PY", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1500px]">
      <PageHeader
        eyebrow="Semana"
        title="Agenda de turnos"
        description="Lun a Sáb · 10:00 a 20:00 hs · Programá turnos y sesiones para todos los profesionales."
        action={
          <div className="flex items-center gap-2">
            {user?.role !== "profesional" && (
              <select
                data-testid="filter-profesional"
                className="input-clinic max-w-[220px]"
                value={filterProf}
                onChange={(e) => setFilterProf(e.target.value)}
              >
                <option value="todos">Todos los profesionales</option>
                {profesionales.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
            <button
              data-testid="nuevo-turno-btn"
              onClick={() => {
                setPrefill(null);
                setModalOpen(true);
              }}
              className="btn-primary-pill"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nuevo turno
            </button>
          </div>
        }
      />

      <div className="card-clinic overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <button
              data-testid="week-prev"
              className="btn-ghost-pill"
              onClick={() => setAnchor(addDays(anchor, -7))}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="week-today"
              className="btn-ghost-pill"
              onClick={() => setAnchor(startOfWeek(new Date()))}
            >
              Hoy
            </button>
            <button
              data-testid="week-next"
              className="btn-ghost-pill"
              onClick={() => setAnchor(addDays(anchor, 7))}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="text-sm font-semibold text-neutral-700 tracking-tight">{rangeLabel}</div>
          <div className="hidden md:flex flex-wrap gap-2 max-w-[40%] justify-end">
            {profesionales.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color || "#6B8E6B" }} />
                {p.name}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-[64px_repeat(6,minmax(0,1fr))] text-sm">
          <div className="border-r border-b border-neutral-200 bg-neutral-50/50" />
          {weekDays.map((d, idx) => (
            <div key={idx} className="text-center border-r border-b border-neutral-200 py-3 bg-neutral-50/50">
              <div className="overline text-neutral-500">{DAYS[idx]}</div>
              <div className="text-base font-bold tracking-tight text-neutral-900">
                {d.getDate()}
              </div>
            </div>
          ))}

          {HOURS.map((h) => (
            <div key={h} className="contents">
              <div className="text-xs text-neutral-500 font-mono px-2 py-3 border-r border-b border-neutral-200 text-right pr-3">
                {String(h).padStart(2, "0")}:00
              </div>
              {weekDays.map((d, dIdx) => {
                const dateStr = ymd(d);
                const items = cellTurnos(dateStr, h);
                return (
                  <button
                    key={dIdx}
                    data-testid={`slot-${dateStr}-${h}`}
                    onClick={() => openCreate(dateStr, `${String(h).padStart(2, "0")}:00`)}
                    className="border-r border-b border-neutral-200 min-h-[70px] p-1.5 text-left hover:bg-neutral-50 transition-colors relative"
                  >
                    <div className="space-y-1">
                      {items.map((t) => (
                        <div
                          key={t.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPrefill({ id: t.id });
                            setModalOpen(true);
                          }}
                          data-testid={`turno-chip-${t.id}`}
                          className="rounded-md px-2 py-1 text-[11px] font-medium leading-tight text-white truncate cursor-pointer hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: t.profesional_color || "#6B8E6B" }}
                          title={`${t.paciente_nombre} · ${t.profesional_nombre}`}
                        >
                          {t.confirmado_por_paciente ? "✓ " : ""}{t.hora} · {t.paciente_nombre}
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {modalOpen && (() => {
        const existing = prefill?.id ? turnos.find((t) => t.id === prefill.id) : null;
        if (existing) {
          return (
            <TurnoViewPanel
              turno={existing}
              onClose={() => setModalOpen(false)}
              onSaved={() => {
                setModalOpen(false);
                load();
              }}
              onDelete={async (id) => {
                try {
                  await api.delete(`/turnos/${id}`);
                  toast.success("Turno eliminado");
                  setModalOpen(false);
                  load();
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
            />
          );
        }
        return (
          <TurnoModal
            onClose={() => setModalOpen(false)}
            onSaved={() => {
              setModalOpen(false);
              load();
            }}
            prefill={prefill}
            pacientes={pacientes}
            profesionales={profesionales}
            currentUser={user}
          />
        );
      })()}
    </div>
  );
}

function TurnoModal({ onClose, onSaved, prefill, pacientes, profesionales, currentUser }) {
  const [paciente_id, setPacienteId] = useState("");
  const [profesional_id, setProfId] = useState(
    currentUser?.role === "profesional" ? currentUser.id : profesionales[0]?.id || ""
  );
  const [fecha, setFecha] = useState(prefill?.fecha || new Date().toISOString().slice(0, 10));
  const [hora, setHora] = useState(prefill?.hora || "10:00");
  const [tipo, setTipo] = useState("sesion");
  const [titulo, setTitulo] = useState("");
  const [notas, setNotas] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(null); // saved turno (server response)

  const isSinPaciente = tipo === "reunion" || tipo === "consulta_externa";

  const save = async () => {
    if (!profesional_id) {
      toast.error("Elegí un profesional");
      return;
    }
    if (isSinPaciente) {
      if (!titulo.trim()) {
        toast.error("Ingresá un título para la reunión / consulta externa");
        return;
      }
    } else if (!paciente_id) {
      toast.error("Elegí un paciente");
      return;
    }
    setLoading(true);
    try {
      const body = { profesional_id, fecha, hora, tipo, notas };
      if (isSinPaciente) body.titulo = titulo;
      else body.paciente_id = paciente_id;
      const { data } = await api.post("/turnos", body);
      toast.success("Turno creado");
      // enrich data with names for WhatsApp message
      const pac = pacientes.find((p) => p.id === paciente_id);
      const prof = profesionales.find((p) => p.id === profesional_id);
      setSaved({
        ...data,
        paciente_nombre: pac ? `${pac.nombre} ${pac.apellido}` : (data.titulo || ""),
        paciente_telefono: pac?.telefono,
        profesional_nombre: prof?.name,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // After save: show confirmation panel with WhatsApp button
  if (saved) {
    return (
      <TurnoViewPanel
        turno={saved}
        onClose={() => {
          onSaved();
        }}
        onSaved={onSaved}
        justCreated
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg border border-neutral-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="overline">Nuevo turno</div>
            <h3 className="text-lg font-bold tracking-tight">Programar consulta o reunión</h3>
          </div>
          <button data-testid="modal-close" onClick={onClose} className="btn-ghost-pill p-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Tipo</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: "sesion", l: "Sesión" },
                { v: "origen", l: "Test ORIGEN" },
                { v: "reunion", l: "Reunión" },
                { v: "consulta_externa", l: "Consulta externa" },
                { v: "otro", l: "Otro" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setTipo(o.v)}
                  data-testid={`turno-tipo-${o.v}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    tipo === o.v
                      ? "border-[hsl(var(--sage))] bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]"
                      : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          {isSinPaciente ? (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Título</label>
              <input
                data-testid="turno-titulo"
                className="input-clinic"
                placeholder={tipo === "reunion" ? "Ej: Reunión de equipo" : "Ej: Consulta externa - Hospital"}
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Paciente</label>
              <select
                data-testid="turno-paciente"
                className="input-clinic"
                value={paciente_id}
                onChange={(e) => setPacienteId(e.target.value)}
              >
                <option value="">Seleccionar paciente…</option>
                {pacientes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} {p.apellido}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Fecha</label>
              <input
                data-testid="turno-fecha"
                type="date"
                className="input-clinic"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Hora</label>
              <select
                data-testid="turno-hora"
                className="input-clinic"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
              >
                {HOURS.map((h) => (
                  <option key={h} value={`${String(h).padStart(2, "0")}:00`}>
                    {String(h).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Profesional</label>
            <select
              data-testid="turno-profesional"
              className="input-clinic"
              value={profesional_id}
              onChange={(e) => setProfId(e.target.value)}
              disabled={currentUser?.role === "profesional"}
            >
              {profesionales.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Notas (opcional)</label>
            <textarea
              data-testid="turno-notas"
              className="input-clinic min-h-[72px]"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-neutral-200 bg-neutral-50/50 rounded-b-2xl">
          <button data-testid="turno-cancel" onClick={onClose} className="btn-outline-pill">
            Cancelar
          </button>
          <button data-testid="turno-guardar" onClick={save} disabled={loading} className="btn-primary-pill">
            {loading ? "Guardando…" : "Guardar turno"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// TurnoViewPanel: shown for existing turnos and right after creation.
// -----------------------------------------------------------------------------
function TurnoViewPanel({ turno, onClose, onDelete, onSaved, justCreated = false }) {
  const cfg = useConfig();
  const [copied, setCopied] = useState(false);
  const [updating, setUpdating] = useState(false);
  const hasPatient = !!turno.paciente_id;
  const waLink = buildWhatsAppLink(turno, cfg);
  const confirmUrl = `${window.location.origin}/confirmar/${turno.confirmation_token}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(confirmUrl);
      setCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No pude copiar el link");
    }
  };

  const changeEstado = async (nuevo) => {
    setUpdating(true);
    try {
      await api.patch(`/turnos/${turno.id}`, { estado: nuevo });
      toast.success("Estado actualizado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUpdating(false);
    }
  };

  const TIPO_LABEL = {
    origen: "Test ORIGEN",
    sesion: "Sesión",
    reunion: "Reunión",
    consulta_externa: "Consulta externa",
    otro: "Otro",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md border border-neutral-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="overline">{justCreated ? "Turno creado" : "Detalle del turno"}</div>
            <h3 className="text-lg font-bold tracking-tight">{turno.paciente_nombre}</h3>
          </div>
          <button data-testid="modal-close" onClick={onClose} className="btn-ghost-pill p-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="overline mb-1">Fecha</div>
              <div className="font-semibold text-neutral-900">{turno.fecha}</div>
            </div>
            <div>
              <div className="overline mb-1">Hora</div>
              <div className="font-semibold text-neutral-900 font-mono">{turno.hora}</div>
            </div>
            <div>
              <div className="overline mb-1">Profesional</div>
              <div className="font-semibold text-neutral-900 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: turno.profesional_color || "#6B8E6B" }} />
                {turno.profesional_nombre}
              </div>
            </div>
            <div>
              <div className="overline mb-1">Tipo</div>
              <div className="font-semibold text-neutral-900">{TIPO_LABEL[turno.tipo] || turno.tipo}</div>
            </div>
            <div>
              <div className="overline mb-1">Estado</div>
              <div className="font-semibold capitalize text-neutral-900">
                {turno.estado || "programado"}
              </div>
            </div>
            <div>
              <div className="overline mb-1">Confirmación</div>
              <div className={`text-sm font-semibold ${turno.confirmado_por_paciente ? "text-[hsl(var(--sage-hover))]" : "text-neutral-500"}`}>
                {turno.confirmado_por_paciente ? "✓ Confirmado" : "Pendiente"}
              </div>
            </div>
          </div>

          {hasPatient && (
            <div className="border-t border-neutral-200 pt-4">
              <div className="overline mb-3 flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5" /> Enviar confirmación al paciente
              </div>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white font-medium px-5 py-3 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                {turno.paciente_telefono ? "Enviar por WhatsApp" : "Abrir WhatsApp (elegir contacto)"}
              </a>
              {!turno.paciente_telefono && (
                <p className="text-xs text-[hsl(var(--terracotta))] mt-2">
                  El paciente no tiene teléfono cargado — se abrirá WhatsApp con el mensaje listo para pegar.
                </p>
              )}
              <button
                onClick={copyLink}
                data-testid="copy-link-btn"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full border border-neutral-300 hover:border-neutral-500 text-neutral-800 px-5 py-2.5 text-sm font-medium transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Link copiado" : "Copiar link de confirmación"}
              </button>
            </div>
          )}

          {!justCreated && (
            <div className="border-t border-neutral-200 pt-4">
              <div className="overline mb-2">Cambiar estado</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "asistio", l: "Asistió" },
                  { v: "ausente", l: "Ausente" },
                  { v: "programado", l: "Programado" },
                  { v: "cancelado", l: "Cancelar" },
                ].map((o) => (
                  <button
                    key={o.v}
                    disabled={updating || turno.estado === o.v}
                    onClick={() => changeEstado(o.v)}
                    data-testid={`view-estado-${o.v}`}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      turno.estado === o.v
                        ? "border-[hsl(var(--sage))] bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]"
                        : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 bg-neutral-50/50 rounded-b-2xl">
          {!justCreated ? (
            <button
              data-testid="turno-delete"
              onClick={() => onDelete(turno.id)}
              className="text-sm text-[hsl(var(--terracotta))] hover:underline flex items-center gap-1.5"
            >
              <Trash2 className="w-4 h-4" /> Eliminar
            </button>
          ) : (
            <span />
          )}
          <button onClick={onClose} className="btn-primary-pill">
            {justCreated ? "Listo" : "Cerrar"}
          </button>
        </div>
      </div>
    </div>
  );
}

